import React, { useMemo, useEffect, useState } from "react";
import { useGetAllTabsUserHasAccessToQuery, useGetIfUserIsAdminQuery } from "../redux/api/user";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "antd";
import SettingsLandingPage from "../components/Settings/SettingsLandingPage";
import {
  BarChartOutlined,
  DollarOutlined,
  ShopOutlined,
  LayoutOutlined,
  HistoryOutlined,
  MoneyCollectOutlined,
  UserOutlined,
  CalculatorOutlined,
  BranchesOutlined,
} from "@ant-design/icons";

const settingsCategoriesBase = [
  {
    key: "page_content_settings",
    title: "Page Content",
    description: "Customize the layout and content of various pages in the application",
    icon: <LayoutOutlined style={{ fontSize: '24px' }} />,
    path: "/settings/page-content",
    color: "#52c41a"
  },
  {
    key: "product_grouping_settings",
    title: "Product Grouping",
    description: "Manage product categorization and grouping for both Shopify and Amazon platforms",
    icon: <ShopOutlined style={{ fontSize: '24px' }} />,
    path: "/settings/product-grouping",
    color: "#722ed1"
  },
  {
    key: "budget_planner_settings",
    title: "Target Settings",
    description: "Configure spend and revenue targets across channels",
    icon: <DollarOutlined style={{ fontSize: '24px' }} />,
    path: "/settings/target-settings",
    color: "#fa8c16"
  },
  {
    key: "marketing_settings",
    title: "Marketing Settings",
    description: "Manage settings for Google, Meta, TikTok, and Bing advertising platforms",
    icon: <BarChartOutlined style={{ fontSize: '24px' }} />,
    path: "/settings/marketing",
    color: "#eb2f96"
  },
  {
    key: "extra_costs_settings",
    title: "Extra Costs",
    description: "Configure additional cost tracking and management settings",
    icon: <MoneyCollectOutlined style={{ fontSize: '24px' }} />,
    path: "/settings/extra-costs",
    color: "#13c2c2"
  },
  {
    key: "campaign_mappings_settings",
    title: "Campaign Mappings",
    description: "Define and manage mappings from ad campaigns to internal categories",
    icon: <BranchesOutlined style={{ fontSize: '24px' }} />,
    path: "/settings/campaign-mappings",
    color: "#1890ff"
  },
  {
    key: "compass_multiplier_settings",
    title: "Compass Multipliers Settings",
    description: "Configure the multipliers for the Compass platform",
    icon: <CalculatorOutlined style={{ fontSize: '24px' }} />,
    path: "/settings/compass-multipliers",
    color: "#13c2c2"
  },
  {
    key: "legacy_settings",
    title: "Legacy Settings",
    description: "Access all legacy configuration options in the traditional settings interface",
    icon: <HistoryOutlined style={{ fontSize: '24px' }} />,
    path: "/legacy_settings",
    color: "#8c8c8c"
  }
];

const alwaysEnabledKeys = ["legacy_settings", "admin_settings"];

const adminCategory = {
  key: "admin_settings",
  title: "Admin Settings",
  description: "Admin-only configuration and controls.",
  icon: <UserOutlined style={{ fontSize: '24px' }} />,
  path: "/settings/admin",
  color: "#d4380d"
};

const SettingsLandingPageContainer = () => {
  const navigate = useNavigate();
  const allowedTabsQuery = useGetAllTabsUserHasAccessToQuery();
  const { data: isAdmin } = useGetIfUserIsAdminQuery();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        allowedTabsQuery.isLoading === false &&
        allowedTabsQuery.isSuccess &&
        allowedTabsQuery.isError === false
      ) {
        setIsLoading(false);
      }
    }, 1000); // Minimum 1 second display
    return () => clearTimeout(timer);
  }, [allowedTabsQuery.isLoading, allowedTabsQuery.isSuccess, allowedTabsQuery.isError]);

  const allowedTabsSet = useMemo(() => new Set(allowedTabsQuery.data || []), [allowedTabsQuery.data]);

  const settingsCategories = useMemo(() => {
    let base = [...settingsCategoriesBase];
    if (isAdmin) {
      base.unshift(adminCategory);
    }
    return base.map((cat) => ({
      ...cat,
      disabled: !allowedTabsSet.has(cat.key) && !alwaysEnabledKeys.includes(cat.key) && !isLoading,
    }));
  }, [allowedTabsSet, isLoading, isAdmin]);

  const handleCardClick = (path) => {
    navigate(path);
  };

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 8 }} style={{ margin: 32 }} />;
  }

  return (
    <SettingsLandingPage
      settingsCategories={settingsCategories}
      onCardClick={handleCardClick}
    />
  );
};

export default SettingsLandingPageContainer; 